// ─────────────────────────────────────────────────────────────────────────────
// Global CSS injection for ContextualAIAssistant (ESG-only)
// Injects minimal global styles (animations, scrollbar) once on mount.
// All component-level styles are inline to avoid CSS conflicts.
// ─────────────────────────────────────────────────────────────────────────────

const STYLE_ID = "cai-global-styles";

const CSS = `
  /* Brand tokens */
  :root {
    --cai-primary: #482A7A;
    --cai-secondary: #B31F7E;
    --cai-bg-tint: #F5F3F8;
    --cai-border: #E5DEF2;
    --cai-text: #1E1730;
    --cai-muted: #6B7280;
  }

  /* Typing indicator dots */
  .cai-typing-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: linear-gradient(135deg, #482A7A, #B31F7E);
    animation: cai-bounce 1.2s ease-in-out infinite;
  }
  @keyframes cai-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-6px); opacity: 1; }
  }

  /* Floating widget open animation */
  @keyframes cai-slide-up {
    from { opacity: 0; transform: translateY(16px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .cai-panel-enter {
    animation: cai-slide-up 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    transform-origin: bottom right;
  }

  /* Message bubble enter */
  @keyframes cai-msg-enter {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .cai-msg-enter {
    animation: cai-msg-enter 0.18s ease-out forwards;
  }

  /* Thin scrollbar */
  .cai-scroll {
    scrollbar-width: thin;
    scrollbar-color: #D4C8E6 transparent;
  }
  .cai-scroll::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .cai-scroll::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #D4C8E6, #B5A4D8);
    border-radius: 3px;
  }
  .cai-scroll::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #B5A4D8, #8E6FBE);
  }

  /* Pulse for context-loading badge */
  @keyframes cai-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }
  .cai-loading-pulse {
    animation: cai-pulse 1.5s ease-in-out infinite;
  }

  /* FAB ring pulse — when context is detected */
  @keyframes cai-fab-ring {
    0%   { box-shadow: 0 0 0 0 rgba(72, 42, 122, 0.45), 0 8px 24px rgba(72, 42, 122, 0.35); }
    70%  { box-shadow: 0 0 0 14px rgba(72, 42, 122, 0), 0 8px 24px rgba(72, 42, 122, 0.35); }
    100% { box-shadow: 0 0 0 0 rgba(72, 42, 122, 0), 0 8px 24px rgba(72, 42, 122, 0.35); }
  }
  .cai-fab-pulse {
    animation: cai-fab-ring 2.4s ease-in-out infinite;
  }

  /* Quick-action chip hover lift */
  .cai-chip {
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, color 0.15s ease;
  }
  .cai-chip:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(72, 42, 122, 0.15);
  }

  /* Suggested prompt card hover */
  .cai-prompt-card {
    transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
  }
  .cai-prompt-card:hover {
    border-color: #482A7A !important;
    background: #FAF7FF !important;
    transform: translateX(2px);
  }

  /* Tool call card disclosure */
  .cai-tool-card {
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .cai-tool-card:hover {
    border-color: #D4C8E6;
    box-shadow: 0 2px 8px rgba(72, 42, 122, 0.08);
  }

  /* Transparency panel */
  .cai-transparency-toggle {
    cursor: pointer;
    color: #6B7280;
    font-size: 11px;
    background: none;
    border: none;
    padding: 4px 0 0 0;
    text-decoration: none;
    transition: color 0.15s ease;
  }
  .cai-transparency-toggle:hover {
    color: #482A7A;
    text-decoration: underline;
  }

  /* Subtle fade for the gradient header */
  @keyframes cai-shimmer {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  .cai-header-gradient {
    background: linear-gradient(135deg, #482A7A 0%, #6B2A8E 50%, #B31F7E 100%);
    background-size: 200% 200%;
    animation: cai-shimmer 8s ease-in-out infinite;
  }

  /* Send button hover */
  .cai-send-btn {
    transition: transform 0.12s ease, background 0.15s ease, box-shadow 0.15s ease;
  }
  .cai-send-btn:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(72, 42, 122, 0.32);
  }
  .cai-send-btn:not(:disabled):active {
    transform: translateY(0);
  }

  /* Header button hover */
  .cai-header-btn {
    transition: background 0.15s ease, transform 0.12s ease;
  }
  .cai-header-btn:hover:not(:disabled) {
    background: rgba(255,255,255,0.25) !important;
    transform: scale(1.05);
  }

  /* ── Thinking indicator (pulsing gradient orb + animated label) ─────────── */
  .cai-thinking {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 2px 0;
    user-select: none;
  }
  .cai-thinking-orb {
    position: relative;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
  .cai-thinking-orb-core {
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: linear-gradient(135deg, #482A7A 0%, #B31F7E 100%);
    box-shadow: 0 0 12px rgba(179, 31, 126, 0.55);
    animation: cai-orb-breathe 1.5s ease-in-out infinite;
  }
  .cai-thinking-orb-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1.5px solid transparent;
    background:
      conic-gradient(from 0deg, rgba(72, 42, 122, 0.0), rgba(179, 31, 126, 0.85), rgba(72, 42, 122, 0.0))
      border-box;
    -webkit-mask:
      linear-gradient(#fff 0 0) padding-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    animation: cai-orb-spin 1.6s linear infinite;
  }
  .cai-thinking-orb-spark {
    position: absolute;
    top: -1px;
    left: 50%;
    width: 4px;
    height: 4px;
    margin-left: -2px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 6px rgba(255, 255, 255, 0.95), 0 0 12px rgba(179, 31, 126, 0.7);
    transform-origin: 2px 12px;
    animation: cai-orb-spark 1.6s linear infinite;
  }
  @keyframes cai-orb-breathe {
    0%, 100% { transform: scale(1); opacity: 1; }
    50%      { transform: scale(0.86); opacity: 0.85; }
  }
  @keyframes cai-orb-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes cai-orb-spark {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  .cai-thinking-text {
    font-size: 12px;
    font-weight: 600;
    background: linear-gradient(90deg, #482A7A 0%, #B31F7E 50%, #482A7A 100%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: cai-thinking-shimmer 2.4s linear infinite;
    display: inline-flex;
    align-items: baseline;
    gap: 1px;
    letter-spacing: -0.005em;
  }
  @keyframes cai-thinking-shimmer {
    from { background-position: 0% 50%; }
    to   { background-position: -200% 50%; }
  }
  .cai-thinking-ellipsis {
    display: inline-flex;
    margin-left: 2px;
  }
  .cai-thinking-ellipsis span {
    opacity: 0.25;
    animation: cai-ellipsis-pulse 1.4s ease-in-out infinite;
    color: #B31F7E;
    -webkit-text-fill-color: #B31F7E;
  }
  .cai-thinking-ellipsis span:nth-child(2) { animation-delay: 0.18s; }
  .cai-thinking-ellipsis span:nth-child(3) { animation-delay: 0.36s; }
  @keyframes cai-ellipsis-pulse {
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40%           { opacity: 1;    transform: translateY(-1px); }
  }

  /* Markdown table inside assistant bubble */
  .cai-md-table {
    border-collapse: collapse;
    width: 100%;
    font-size: 12px;
  }
  .cai-md-table thead {
    background: linear-gradient(135deg, rgba(72,42,122,0.06), rgba(179,31,126,0.06));
  }
  .cai-md-table th, .cai-md-table td {
    padding: 7px 10px;
    text-align: left;
    border-bottom: 1px solid #F3EEFA;
  }
  .cai-md-table th {
    color: #482A7A;
    font-weight: 700;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #EAE3F4;
  }
  .cai-md-table tbody tr:nth-child(even) {
    background: #FBFAFD;
  }
`;

export function injectGlobalStyles(): void {
	if (typeof document === "undefined") return;
	if (document.getElementById(STYLE_ID)) return;
	const style = document.createElement("style");
	style.id = STYLE_ID;
	style.textContent = CSS;
	document.head.appendChild(style);
}

export function removeGlobalStyles(): void {
	if (typeof document === "undefined") return;
	document.getElementById(STYLE_ID)?.remove();
}
